<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import SaveToLibrary from '@/library/SaveToLibrary.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { getSavedCopiesOfStrategy } from '@/library/api'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions } from '@/util/access_visibility'
import { toolkitUrls } from '@/util/links'
import { keepingBetaOptIn } from '@/util/beta_opt_in'

const SAVE_RESULT_DISPLAY_MS = 5000

type SaveResult =
  | { succeeded: true, folderName: string, savedStrategyUuid: string }
  | { succeeded: false, errorMessage: string }

const props = defineProps({
  strategyUuid: {
    type: String,
    required: true
  },
  strategyTitle: {
    type: String,
    default: ''
  }
})

const { userId, authInfo } = storeToRefs(useUserStore())

const savePermissions = computed(() =>
  actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.STRATEGY, {}, authInfo.value)
)

const savedCopyCount = ref(0)
let latestCountRequest = 0

async function refreshSavedCopyCount() {
  const thisRequest = ++latestCountRequest
  if (!userId.value) return
  try {
    const savedCopies = await getSavedCopiesOfStrategy(userId.value, props.strategyUuid)
    if (thisRequest !== latestCountRequest) return
    savedCopyCount.value = savedCopies.length
  } catch (error) {
    console.error('Error loading saved copies of strategy:', error)
  }
}

watch([userId, () => props.strategyUuid], () => {
  savedCopyCount.value = 0
  refreshSavedCopyCount()
}, { immediate: true })

const saveResult = ref<SaveResult | null>(null)
let saveResultTimer: ReturnType<typeof setTimeout> | undefined

function showSaveResult(result: SaveResult) {
  clearTimeout(saveResultTimer)
  saveResult.value = result
  saveResultTimer = setTimeout(dismissSaveResult, SAVE_RESULT_DISPLAY_MS)
}

function dismissSaveResult() {
  clearTimeout(saveResultTimer)
  saveResult.value = null
}

onBeforeUnmount(() => clearTimeout(saveResultTimer))

function handleSave(folder: { uuid: string, name: string }, savedStrategy: unknown) {
  showSaveResult({
    succeeded: true,
    folderName: folder.name,
    savedStrategyUuid: (savedStrategy as { uuid: string }).uuid
  })
  refreshSavedCopyCount()
}

function handleSaveError(error: unknown) {
  showSaveResult({ succeeded: false, errorMessage: (error as Error).message })
}
</script>

<template>
  <div class="rounded-xs bg-gray-100 p-4">
    <SaveToLibrary
      :content-type="CONTENT_TYPES.STRATEGY"
      content-type-label="Strategy Page"
      :content-uuid="strategyUuid"
      :content-title="strategyTitle"
      :enable-notes="true"
      notes-placeholder="How would you implement this strategy?"
      size="lg"
      :full-width="true"
      :action-permissions="savePermissions"
      @save="handleSave"
      @saveerror="handleSaveError"
    />

    <div v-if="savedCopyCount > 0" class="mt-3 text-center text-text-md text-gray-700">
      <span class="text-text-2xl font-bold">{{ savedCopyCount }}</span>
      {{ savedCopyCount === 1 ? 'version' : 'versions' }} of this page {{ savedCopyCount === 1 ? 'is' : 'are' }}
      <br />saved in <router-link :to="{ name: 'AllFolders' }" class="text-blue-400 hover:underline">Your Library</router-link>.
    </div>

    <Transition
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
      enter-active-class="transition-opacity motion-reduce:transition-none"
      leave-active-class="transition-opacity motion-reduce:transition-none"
    >
      <GbInlineMessage
        v-if="saveResult"
        :gb-theme="saveResult.succeeded ? 'green' : 'red'"
        class="mt-2 w-full"
        :pt="{ text: { class: 'flex-1' } }"
        :pt-options="{ mergeProps: true }"
      >
        <div class="flex items-start gap-2 text-text-md">
          <span v-if="saveResult.succeeded" class="flex-1 text-center">
            Saved to <span class="fs-exclude">{{ saveResult.folderName }}</span>!
            <a :href="keepingBetaOptIn(toolkitUrls.libraryStrategyPage(saveResult.savedStrategyUuid))" class="text-blue-400 hover:underline">View saved strategy page</a>
          </span>
          <span v-else class="flex-1 text-center">{{ saveResult.errorMessage }}</span>
          <button
            type="button"
            aria-label="Close"
            class="material-icons-round text-text-xl leading-none text-gray-700 hover:text-gray-900"
            @click="dismissSaveResult"
          >close</button>
        </div>
      </GbInlineMessage>
    </Transition>
  </div>
</template>
